import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Dumbbell, ChevronRight, ChevronLeft, Check, Camera, Phone, Mail, ChevronDown, Upload, X, FileText, Users } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { uploadBusinessDoc, updateBusinessDocUrl } from '../lib/trainerDb';

function formatBusinessNumber(value) {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

function formatPhone(value) {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

function toLoginEmail(input) {
  if (input.includes('@')) return input;
  const digits = input.replace(/\D/g, '');
  return digits + '@gymapp.local';
}

export default function Auth() {
  const {
    signIn, signUp, signUpTrainer, signUpTrainerViaInvite, demoLogin,
    findGymByCode, lookupMemberByPhone, linkMemberAccount,
    lookupTrainerByPhone,
    resetPasswordForEmail,
  } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [mode, setMode] = useState('role_select'); // role_select | login | member | trainer | trial | reset | owner
  const [showContact, setShowContact] = useState(false);

  // ── 비밀번호 재설정 ──
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState('');

  // ── 로그인 ──
  const [loginId, setLoginId] = useState('');
  const [loginPw, setLoginPw] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // ── 회원 가입 (다단계) ──
  const [step, setStep] = useState('gym_code');
  const [gymCode, setGymCode] = useState('');
  const [gymInfo, setGymInfo] = useState(null);
  const [phone, setPhone] = useState('');
  const [foundMember, setFoundMember] = useState(null);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [memberError, setMemberError] = useState('');
  const [memberLoading, setMemberLoading] = useState(false);

  // ── 트레이너 로그인 ──
  const [trainerStep, setTrainerStep] = useState('gym_code'); // gym_code | phone | signin | signup
  const [trainerGymCode, setTrainerGymCode] = useState('');
  const [trainerGymInfo, setTrainerGymInfo] = useState(null);
  const [trainerPhone, setTrainerPhone] = useState('');
  const [trainerRecord, setTrainerRecord] = useState(null);
  const [trainerPw, setTrainerPw] = useState('');
  const [trainerPwConfirm, setTrainerPwConfirm] = useState('');
  const [trainerError, setTrainerError] = useState('');
  const [trainerLoading, setTrainerLoading] = useState(false);

  // ── 트레이너 초대 코드 가입 ──
  const [tiStep, setTiStep] = useState('code'); // code | info | signup
  const [tiCode, setTiCode] = useState('');
  const [tiInfo, setTiInfo] = useState(null); // { trainerName, gymName }
  const [tiEmail, setTiEmail] = useState('');
  const [tiPw, setTiPw] = useState('');
  const [tiPwConfirm, setTiPwConfirm] = useState('');
  const [tiError, setTiError] = useState('');
  const [tiLoading, setTiLoading] = useState(false);

  // ── 무료 체험 신청 ──
  const [trialName, setTrialName] = useState('');
  const [trialGym, setTrialGym] = useState('');
  const [trialPhone, setTrialPhone] = useState('');
  const [trialEmail, setTrialEmail] = useState('');
  const [trialLoading, setTrialLoading] = useState(false);
  const [trialSuccess, setTrialSuccess] = useState(false);
  const [trialError, setTrialError] = useState('');

  // ── 대표자 가입 ──
  const [ownerStep, setOwnerStep] = useState('code'); // code | info
  const [inviteCode, setInviteCode] = useState('');
  const [inviteCodeError, setInviteCodeError] = useState('');
  const [inviteCodeLoading, setInviteCodeLoading] = useState(false);
  // 대표자 기본 정보
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerPw, setOwnerPw] = useState('');
  const [ownerPwConfirm, setOwnerPwConfirm] = useState('');
  // 사업장 정보
  const [ownerPhone, setOwnerPhone] = useState('');
  const [ownerBusinessName, setOwnerBusinessName] = useState('');
  const [ownerTaxEmail, setOwnerTaxEmail] = useState('');
  const [ownerBusinessNumber, setOwnerBusinessNumber] = useState('');
  const [ownerBusinessAddress, setOwnerBusinessAddress] = useState('');
  const [ownerBusinessAddressDetail, setOwnerBusinessAddressDetail] = useState('');
  const [ownerBusinessPhone, setOwnerBusinessPhone] = useState('');
  // 사업자등록증 업로드
  const [businessDoc, setBusinessDoc] = useState(null);
  const [businessDocName, setBusinessDocName] = useState('');
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [emailChecked, setEmailChecked] = useState(false);
  const [emailCheckMsg, setEmailCheckMsg] = useState('');
  const [emailCheckLoading, setEmailCheckLoading] = useState(false);

  const [ownerError, setOwnerError] = useState('');
  const [ownerLoading, setOwnerLoading] = useState(false);

  // ── URL 파라미터로 초대 코드 자동 처리 ──
  useEffect(() => {
    const urlCode = searchParams.get('code');
    const trainerCode = searchParams.get('trainer_code');
    const memberCode = searchParams.get('member_code');

    if (memberCode) {
      // 회원 가입 링크: 매장 코드 자동 채움 + 가입 플로우 시작
      setMode('member');
      setStep('gym_code');
      setGymCode(memberCode.toUpperCase());
      findGymByCode(memberCode.toUpperCase()).then(gym => {
        if (gym) {
          setGymInfo(gym);
          setStep('phone');
        } else {
          setMemberError('유효하지 않은 매장 코드입니다.');
        }
      });
    } else if (trainerCode) {
      const code = trainerCode.toUpperCase();
      setMode('trainer_invite');
      setTiCode(code);
      supabase.rpc('validate_trainer_invite', { p_code: code }).then(({ data }) => {
        if (data) {
          setTiInfo(data);
          setTiStep('info');
        } else {
          setTiError('유효하지 않거나 만료된 초대 링크입니다.');
        }
      });
    } else if (urlCode) {
      setMode('owner');
      const code = urlCode.toUpperCase();
      setInviteCode(code);
      supabase.rpc('validate_invite_code', { p_code: code }).then(({ data }) => {
        if (data) {
          setOwnerStep('info');
        } else {
          setInviteCodeError('유효하지 않거나 만료된 초대 링크입니다.');
        }
      });
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 로그인 ──
  async function handleLogin(e) {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    const { error } = await signIn({ email: toLoginEmail(loginId), password: loginPw });
    if (error) setLoginError('아이디 또는 비밀번호가 올바르지 않습니다.');
    setLoginLoading(false);
  }

  // ── 회원 가입 ──
  async function handleGymCode(e) {
    e.preventDefault();
    setMemberError('');
    setMemberLoading(true);
    const gym = await findGymByCode(gymCode.trim().toUpperCase());
    if (!gym) { setMemberError('유효하지 않은 매장 코드입니다.'); setMemberLoading(false); return; }
    setGymInfo(gym);
    setStep('phone');
    setMemberLoading(false);
  }

  async function handlePhone(e) {
    e.preventDefault();
    setMemberError('');
    // 전화번호 형식 검증 (010 + 8자리)
    const digits = phone.replace(/\D/g, '');
    if (!/^01[016789]\d{7,8}$/.test(digits)) {
      setMemberError('올바른 전화번호 형식이 아닙니다. (예: 010-1234-5678)');
      return;
    }
    setMemberLoading(true);
    const result = await lookupMemberByPhone(gymCode, phone);
    if (result?.hasAccount) {
      setMemberError('이미 계정이 있는 번호입니다.');
      setStep('has_account');
      setFoundMember(result);
      setMemberLoading(false);
      return;
    }
    if (!result) {
      setMemberError('등록되지 않은 번호입니다. 헬스장에 문의해주세요.');
      setMemberLoading(false);
      return;
    }
    setFoundMember(result);
    setStep('found');
    setMemberLoading(false);
  }

  async function handleFoundSignup(e) {
    e.preventDefault();
    setMemberError('');
    if (password !== passwordConfirm) { setMemberError('비밀번호가 일치하지 않습니다.'); return; }
    if (password.length < 6) { setMemberError('비밀번호는 6자 이상이어야 합니다.'); return; }
    setMemberLoading(true);
    const { error } = await signUp({ phone, password, role: 'member', name: foundMember.name });
    if (error) {
      // 이전 시도로 Auth 계정이 이미 존재하는 경우 → 로그인 시도
      if (error.message?.includes('already') || error.message?.includes('already been registered')) {
        const digits = phone.replace(/\D/g, '');
        const { error: signInErr } = await signIn({ email: digits + '@gymapp.local', password });
        if (signInErr) {
          setMemberError('이미 가입된 번호입니다. 비밀번호가 다르면 로그인 화면에서 시도해주세요.');
          setMemberLoading(false);
          return;
        }
        const { error: linkErr } = await linkMemberAccount(gymCode, phone);
        if (linkErr) {
          setMemberError('회원 계정 연결에 실패했습니다. 헬스장에 문의해 주세요.');
          setMemberLoading(false);
          return;
        }
        setStep('success');
        setMemberLoading(false);
        return;
      }
      setMemberError(error.message);
      setMemberLoading(false);
      return;
    }
    const { error: linkErr2 } = await linkMemberAccount(gymCode, phone);
    if (linkErr2) {
      setMemberError('회원 계정 연결에 실패했습니다. 헬스장에 문의해 주세요.');
      setMemberLoading(false);
      return;
    }
    setStep('success');
    setMemberLoading(false);
  }


  // ── 트레이너 로그인 ──
  async function handleTrainerGymCode(e) {
    e.preventDefault();
    setTrainerError('');
    setTrainerLoading(true);
    const gym = await findGymByCode(trainerGymCode.trim().toUpperCase());
    if (!gym) { setTrainerError('유효하지 않은 매장 코드입니다.'); setTrainerLoading(false); return; }
    setTrainerGymInfo(gym);
    setTrainerStep('phone');
    setTrainerLoading(false);
  }

  async function handleTrainerPhone(e) {
    e.preventDefault();
    setTrainerError('');
    setTrainerLoading(true);
    const digits = trainerPhone.replace(/\D/g, '');
    const result = await lookupTrainerByPhone(trainerGymCode, digits);
    if (!result) { setTrainerError('등록된 트레이너를 찾을 수 없습니다. 헬스장 코드와 전화번호를 확인해주세요.'); setTrainerLoading(false); return; }
    setTrainerRecord(result);
    setTrainerStep(result.hasAccount ? 'signin' : 'signup');
    setTrainerLoading(false);
  }

  async function handleTrainerSignIn(e) {
    e.preventDefault();
    setTrainerError('');
    setTrainerLoading(true);
    const digits = trainerPhone.replace(/\D/g, '');
    const { error } = await signIn({ email: digits + '@trainer.local', password: trainerPw });
    if (error) setTrainerError('비밀번호가 올바르지 않습니다.');
    setTrainerLoading(false);
  }

  async function handleTrainerSignUp(e) {
    e.preventDefault();
    setTrainerError('');
    if (trainerPw !== trainerPwConfirm) { setTrainerError('비밀번호가 일치하지 않습니다.'); return; }
    if (trainerPw.length < 6) { setTrainerError('비밀번호는 6자 이상이어야 합니다.'); return; }
    setTrainerLoading(true);
    const { error } = await signUpTrainer({
      gymCode: trainerGymCode,
      phone: trainerPhone,
      password: trainerPw,
      name: trainerRecord.name,
    });
    if (error) { setTrainerError(error.message); setTrainerLoading(false); return; }
    // 로그인
    const digits = trainerPhone.replace(/\D/g, '');
    await signIn({ email: digits + '@trainer.local', password: trainerPw });
    setTrainerLoading(false);
  }

  // ── 무료 체험 신청 ──
  async function handleTrialSubmit(e) {
    e.preventDefault();
    setTrialError('');
    setTrialLoading(true);
    const { error } = await supabase.from('trial_requests').insert({
      name: trialName,
      gym_name: trialGym,
      phone: trialPhone,
      email: trialEmail,
    });
    setTrialLoading(false);
    if (error) { setTrialError('신청 중 오류가 발생했습니다. 다시 시도해주세요.'); return; }
    setTrialSuccess(true);
  }

  // ── 비밀번호 재설정 ──
  async function handleReset(e) {
    e.preventDefault();
    setResetError('');
    setResetLoading(true);
    const { error } = await resetPasswordForEmail(resetEmail);
    setResetLoading(false);
    if (error) setResetError('이메일 전송에 실패했습니다. 이메일 주소를 확인해주세요.');
    else setResetSent(true);
  }

  // ── 초대 코드 검증 ──
  async function handleValidateCode(e) {
    e.preventDefault();
    setInviteCodeError('');
    if (!inviteCode.trim()) { setInviteCodeError('초대 코드를 입력해주세요.'); return; }
    setInviteCodeLoading(true);
    const { data, error } = await supabase.rpc('validate_invite_code', { p_code: inviteCode.trim() });
    setInviteCodeLoading(false);
    if (error || !data) { setInviteCodeError('유효하지 않거나 이미 사용된 초대 코드입니다.'); return; }
    setOwnerStep('info');
  }

  // ── 대표자 가입 ──
  async function handleOwnerSignup(e) {
    e.preventDefault();
    setOwnerError('');
    if (!ownerName.trim()) { setOwnerError('이름을 입력해주세요.'); return; }
    if (!ownerEmail.trim()) { setOwnerError('이메일을 입력해주세요.'); return; }
    if (!emailChecked) { setOwnerError('이메일 중복확인을 해주세요.'); return; }
    if (!ownerPhone.trim()) { setOwnerError('연락처를 입력해주세요.'); return; }
    if (!ownerBusinessName.trim()) { setOwnerError('사업장명을 입력해주세요.'); return; }
    if (ownerPw.length < 6) { setOwnerError('비밀번호는 6자 이상이어야 합니다.'); return; }
    if (ownerPw !== ownerPwConfirm) { setOwnerError('비밀번호가 일치하지 않습니다.'); return; }
    // 사업자등록증은 선택사항 (유료 전환 시 업로드 가능)

    setOwnerLoading(true);
    const { data, error } = await signUp({
      email: ownerEmail.trim(),
      password: ownerPw,
      role: 'gym_owner',
      name: ownerName.trim(),
      phone: ownerPhone.replace(/\D/g, ''),
      business_name: ownerBusinessName.trim(),
      tax_email: ownerTaxEmail.trim() || null,
      business_number: ownerBusinessNumber.replace(/\D/g, '') || null,
      business_address: ownerBusinessAddress.trim()
        ? `${ownerBusinessAddress.trim()}${ownerBusinessAddressDetail.trim() ? ' ' + ownerBusinessAddressDetail.trim() : ''}`
        : null,
      business_phone: ownerBusinessPhone.replace(/\D/g, '') || null,
    });
    if (error) { setOwnerError(error.message || '가입 중 오류가 발생했습니다.'); setOwnerLoading(false); return; }

    // 초대 코드 사용 처리
    const userId = data?.user?.id;
    if (userId) {
      const { error: codeError } = await supabase.rpc('use_invite_code', { p_code: inviteCode.trim(), p_user_id: userId });
      if (codeError) {
        setOwnerError('초대 코드 처리 중 오류가 발생했습니다. 관리자에게 문의해 주세요.');
        setOwnerLoading(false);
        return;
      }
    }

    // 가입 후 자동 로그인
    const { error: signInErr } = await signIn({ email: ownerEmail.trim(), password: ownerPw });
    if (signInErr) {
      setOwnerError('가입은 완료됐으나 자동 로그인에 실패했습니다. 직접 로그인해 주세요.');
      setOwnerLoading(false);
      return;
    }

    // 사업자등록증 업로드 (로그인 후)
    if (businessDoc) {
      const docUrl = await uploadBusinessDoc(businessDoc);
      if (docUrl) await updateBusinessDocUrl(docUrl);
    }

    setOwnerLoading(false);
    // 자동 로그인 후 App.js가 GymSetup(2단계)으로 자동 전환
  }

  async function handleCheckEmail() {
    const email = ownerEmail.trim();
    if (!email) { setEmailCheckMsg('이메일을 입력해주세요.'); return; }
    setEmailCheckLoading(true);
    setEmailCheckMsg('');
    const { data, error } = await supabase.rpc('check_email_exists', { p_email: email });
    setEmailCheckLoading(false);
    if (error) { setEmailCheckMsg('확인 중 오류가 발생했습니다.'); return; }
    if (data) {
      setEmailChecked(false);
      setEmailCheckMsg('이미 사용 중인 이메일입니다.');
    } else {
      setEmailChecked(true);
      setEmailCheckMsg('사용 가능한 이메일입니다.');
    }
  }

  function handleDocFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusinessDoc(file);
    setBusinessDocName(file.name);
  }

  function resetMemberFlow() {
    setStep('member_login');
    setGymCode(''); setGymInfo(null); setPhone(''); setFoundMember(null);
    setPassword(''); setPasswordConfirm('');
    setMemberError('');
  }

  function resetTrainerFlow() {
    setTrainerStep('gym_code');
    setTrainerGymCode(''); setTrainerGymInfo(null); setTrainerPhone(''); setTrainerRecord(null);
    setTrainerPw(''); setTrainerPwConfirm('');
    setTrainerError('');
  }

  // ── 트레이너 초대 코드 핸들러 ──
  async function handleTiValidate(e) {
    e.preventDefault();
    setTiError('');
    if (!tiCode.trim()) { setTiError('초대 코드를 입력해주세요.'); return; }
    setTiLoading(true);
    const { data } = await supabase.rpc('validate_trainer_invite', { p_code: tiCode.trim().toUpperCase() });
    setTiLoading(false);
    if (!data) { setTiError('유효하지 않거나 이미 사용된 초대 코드입니다.'); return; }
    setTiInfo(data);
    setTiStep('info');
  }

  async function handleTiSignup(e) {
    e.preventDefault();
    setTiError('');
    if (!tiEmail.trim()) { setTiError('이메일을 입력해주세요.'); return; }
    if (tiPw.length < 6) { setTiError('비밀번호는 6자 이상이어야 합니다.'); return; }
    if (tiPw !== tiPwConfirm) { setTiError('비밀번호가 일치하지 않습니다.'); return; }
    setTiLoading(true);
    const { error } = await signUpTrainerViaInvite({
      email: tiEmail.trim(),
      password: tiPw,
      name: tiInfo.trainerName,
      inviteCode: tiCode,
    });
    setTiLoading(false);
    if (error) { setTiError(error.message || '가입 중 오류가 발생했습니다.'); return; }
    // 가입 후 자동 로그인
    await signIn({ email: tiEmail.trim().toLowerCase(), password: tiPw });
  }

  function resetTiFlow() {
    setTiStep('code'); setTiCode(''); setTiInfo(null);
    setTiEmail(''); setTiPw(''); setTiPwConfirm('');
    setTiError('');
  }

  function resetOwnerFlow() {
    setOwnerStep('code');
    setInviteCode(''); setInviteCodeError('');
    setOwnerName(''); setOwnerEmail(''); setOwnerPw(''); setOwnerPwConfirm('');
    setOwnerPhone(''); setOwnerBusinessName(''); setOwnerTaxEmail('');
    setOwnerBusinessNumber(''); setOwnerBusinessAddress(''); setOwnerBusinessPhone('');
    setBusinessDoc(null); setBusinessDocName('');
    setOwnerError('');
  }


  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">

          {/* 브랜딩 */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-2xl mb-4">
              <Dumbbell className="w-9 h-9 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">GYM Manager</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">헬스장 회원 관리 시스템</p>
          </div>

          {/* ── 역할 선택 ── */}
          {mode === 'role_select' && (
            <div className="space-y-3">
              <button
                onClick={() => setMode('login')}
                className="w-full card p-5 flex items-center gap-4 hover:border-primary-400 dark:hover:border-primary-500 transition-colors text-left"
              >
                <div className="w-12 h-12 bg-primary-50 dark:bg-primary-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Dumbbell className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">대표자 · 트레이너</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">헬스장 관리자 또는 트레이너로 로그인</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 ml-auto flex-shrink-0" />
              </button>
              <button
                onClick={() => { setStep('member_login'); setPhone(''); setPassword(''); setMemberError(''); setMode('member'); }}
                className="w-full card p-5 flex items-center gap-4 hover:border-primary-400 dark:hover:border-primary-500 transition-colors text-left"
              >
                <div className="w-12 h-12 bg-green-50 dark:bg-green-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">회원</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">헬스장 회원으로 로그인 · 가입</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 ml-auto flex-shrink-0" />
              </button>
            </div>
          )}

          {/* ── 로그인 ── */}
          {mode === 'login' && (
            <div className="card p-6 space-y-4">
              <div className="flex items-center gap-2">
                <button onClick={() => setMode('role_select')} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  <ChevronLeft className="w-5 h-5 text-gray-500" />
                </button>
                <h2 className="font-semibold text-gray-900 dark:text-white">대표자 · 트레이너 로그인</h2>
              </div>
              <form onSubmit={handleLogin} className="space-y-3">
                <div>
                  <label className="label">이메일 또는 전화번호</label>
                  <input
                    className="input"
                    type="text"
                    placeholder="example@email.com 또는 010-0000-0000"
                    value={loginId}
                    onChange={e => {
                      const val = e.target.value;
                      if (val.includes('@') || /[a-zA-Z]/.test(val)) setLoginId(val);
                      else setLoginId(formatPhone(val));
                    }}
                    required
                  />
                </div>
                <div>
                  <label className="label">비밀번호</label>
                  <input
                    type="password"
                    className="input"
                    placeholder="비밀번호"
                    value={loginPw}
                    onChange={e => setLoginPw(e.target.value)}
                    required
                  />
                </div>
                {loginError && <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">{loginError}</p>}
                <button type="submit" className="btn-primary w-full justify-center" disabled={loginLoading}>
                  {loginLoading ? '로그인 중...' : '로그인'}
                </button>
              </form>

              {/* 도입 문의 아코디언 */}
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowContact(v => !v)}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <span className="flex-1 text-center">도입을 고민 중이신가요?</span>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showContact ? 'rotate-180' : ''}`} />
                </button>
                {showContact && (
                  <div className="px-4 pb-4 space-y-3 border-t border-gray-100 dark:border-gray-700 pt-3">
                    <div className="flex flex-col gap-2">
                      <a href="tel:18771827" className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                        <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span>1877-1827</span>
                      </a>
                      <a href="mailto:ezbiz.manager@gmail.com" className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                        <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span>ezbiz.manager@gmail.com</span>
                      </a>
                    </div>
                    <button
                      onClick={() => { setMode('trial'); setTrialSuccess(false); setTrialError(''); }}
                      className="w-full py-2.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
                    >
                      무료 체험 신청하기
                    </button>
                    <button
                      onClick={() => { demoLogin(); navigate('/'); }}
                      className="w-full py-2 rounded-lg text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                    >
                      지금 바로 데모 둘러보기 →
                    </button>
                    <button
                      onClick={() => { resetOwnerFlow(); setMode('owner'); }}
                      className="w-full py-2 rounded-lg text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                      초대 코드로 가입하기
                    </button>
                    <button
                      onClick={() => { resetTrainerFlow(); setMode('trainer'); }}
                      className="w-full py-2 rounded-lg text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                      트레이너 로그인 (기존 계정)
                    </button>
                    <button
                      onClick={() => { resetTiFlow(); setMode('trainer_invite'); }}
                      className="w-full py-2 rounded-lg text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                      트레이너 초대 코드로 가입하기
                    </button>
                  </div>
                )}
              </div>

              <p className="text-center">
                <button
                  onClick={() => { setMode('reset'); setResetEmail(''); setResetSent(false); setResetError(''); }}
                  className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 underline"
                >
                  비밀번호를 잊으셨나요?
                </button>
              </p>
            </div>
          )}

          {/* ── 무료 체험 신청 ── */}
          {mode === 'trial' && (
            <div className="card p-6">
              <div className="flex items-center gap-2 mb-5">
                <button onClick={() => setMode('role_select')} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h2 className="font-semibold text-gray-900 dark:text-white">무료 체험 신청</h2>
              </div>
              {trialSuccess ? (
                <div className="text-center py-4 space-y-3">
                  <div className="w-14 h-14 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
                    <Check className="w-7 h-7 text-green-600 dark:text-green-400" />
                  </div>
                  <p className="font-semibold text-gray-900 dark:text-white">신청이 완료되었습니다!</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    담당자가 확인 후 <span className="font-medium">1~2 영업일 내</span>로 연락드리겠습니다.
                  </p>
                  <button className="btn-primary mx-auto" onClick={() => setMode('role_select')}>확인</button>
                </div>
              ) : (
                <form onSubmit={handleTrialSubmit} className="space-y-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400">아래 정보를 남겨주시면 담당자가 연락드립니다.</p>
                  <div>
                    <label className="label">담당자 이름 <span className="text-red-500">*</span></label>
                    <input className="input" placeholder="홍길동" value={trialName} onChange={e => setTrialName(e.target.value)} required />
                  </div>
                  <div>
                    <label className="label">헬스장명 <span className="text-red-500">*</span></label>
                    <input className="input" placeholder="○○ 피트니스" value={trialGym} onChange={e => setTrialGym(e.target.value)} required />
                  </div>
                  <div>
                    <label className="label">연락처 <span className="text-red-500">*</span></label>
                    <input type="tel" className="input" placeholder="010-0000-0000" value={trialPhone} onChange={e => setTrialPhone(e.target.value)} required />
                  </div>
                  <div>
                    <label className="label">이메일</label>
                    <input type="email" className="input" placeholder="email@example.com" value={trialEmail} onChange={e => setTrialEmail(e.target.value)} />
                  </div>
                  {trialError && <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">{trialError}</p>}
                  <button type="submit" className="btn-primary w-full justify-center" disabled={trialLoading}>
                    {trialLoading ? '신청 중...' : '신청하기'}
                  </button>
                </form>
              )}
            </div>
          )}

          {/* ── 회원 앱 ── */}
          {mode === 'member' && (
            <div className="card p-6">
              <div className="flex items-center gap-2 mb-5">
                <button onClick={() => step === 'member_login' ? setMode('role_select') : resetMemberFlow()} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h2 className="font-semibold text-gray-900 dark:text-white">
                  {step === 'member_login' ? '회원 로그인' : '회원 가입'}
                </h2>
              </div>

              {/* 회원 로그인 (기본 화면) */}
              {step === 'member_login' && (
                <form onSubmit={async e => {
                  e.preventDefault();
                  setMemberError('');
                  setMemberLoading(true);
                  const digits = phone.replace(/\D/g, '');
                  const { error } = await signIn({ email: digits + '@gymapp.local', password });
                  if (error) setMemberError('전화번호 또는 비밀번호가 올바르지 않습니다.');
                  setMemberLoading(false);
                }} className="space-y-4">
                  <div>
                    <label className="label">전화번호</label>
                    <input type="tel" className="input" placeholder="010-0000-0000" value={phone} onChange={e => setPhone(e.target.value)} required />
                  </div>
                  <div>
                    <label className="label">비밀번호</label>
                    <input type="password" className="input" placeholder="비밀번호" value={password} onChange={e => setPassword(e.target.value)} required />
                  </div>
                  {memberError && <p className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400 rounded-lg px-3 py-2">{memberError}</p>}
                  <button type="submit" className="btn-primary w-full" disabled={memberLoading}>
                    {memberLoading ? '로그인 중...' : '로그인'}
                  </button>
                  <p className="text-center text-sm text-gray-400 pt-1">
                    처음이신가요?{' '}
                    <button type="button" onClick={() => { setMemberError(''); setPhone(''); setPassword(''); setStep('gym_code'); }} className="text-primary-600 dark:text-primary-400 font-medium hover:underline">
                      회원 가입
                    </button>
                  </p>
                </form>
              )}

              {/* 가입 단계 진행바 */}
              {!['select', 'success', 'member_login', 'has_account'].includes(step) && (
                <div className="flex items-center gap-1 mb-6">
                  {['매장 코드', '전화번호', '계정 설정'].map((label, i) => {
                    const signupSteps = ['gym_code', 'phone', 'found'];
                    const cur = signupSteps.indexOf(step) === -1 ? 2 : signupSteps.indexOf(step);
                    return (
                      <React.Fragment key={label}>
                        <div className={`flex items-center gap-1 ${i <= cur ? 'text-primary-600 dark:text-primary-400' : 'text-gray-300 dark:text-gray-600'}`}>
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${i < cur ? 'bg-primary-600 text-white' : i === cur ? 'border-2 border-primary-600' : 'border-2 border-gray-300 dark:border-gray-600'}`}>
                            {i < cur ? <Check className="w-3 h-3" /> : i + 1}
                          </div>
                          <span className="text-xs hidden sm:inline">{label}</span>
                        </div>
                        {i < 2 && <div className={`flex-1 h-px ${i < cur ? 'bg-primary-400' : 'bg-gray-200 dark:bg-gray-700'}`} />}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}

              {step === 'gym_code' && (
                <form onSubmit={handleGymCode} className="space-y-4">
                  <div>
                    <label className="label">매장 코드</label>
                    <input className="input text-center text-lg tracking-widest font-mono uppercase" placeholder="XXXXXX" value={gymCode} onChange={e => setGymCode(e.target.value.toUpperCase())} maxLength={6} required />
                    <p className="text-xs text-gray-400 mt-1.5">헬스장에서 받은 6자리 코드를 입력하세요.</p>
                  </div>
                  {memberError && <p className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400 rounded-lg px-3 py-2">{memberError}</p>}
                  <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2" disabled={memberLoading}>
                    {memberLoading ? '확인 중...' : '다음'} <ChevronRight className="w-4 h-4" />
                  </button>
                </form>
              )}

              {step === 'phone' && (
                <form onSubmit={handlePhone} className="space-y-4">
                  <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg px-4 py-3 text-sm text-primary-700 dark:text-primary-300">
                    <span className="font-medium">{gymInfo?.name}</span> 회원이시군요!
                  </div>
                  <div>
                    <label className="label">전화번호</label>
                    <input type="tel" className="input" placeholder="010-0000-0000" value={phone} onChange={e => setPhone(e.target.value)} required />
                  </div>
                  {memberError && <p className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400 rounded-lg px-3 py-2">{memberError}</p>}
                  <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2" disabled={memberLoading}>
                    {memberLoading ? '확인 중...' : '다음'} <ChevronRight className="w-4 h-4" />
                  </button>
                </form>
              )}

              {step === 'has_account' && foundMember && (
                <div className="space-y-4">
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg px-4 py-3">
                    <p className="text-sm font-medium text-blue-700 dark:text-blue-400">{foundMember.name}님, 이미 가입된 계정이 있습니다.</p>
                    <p className="text-xs text-blue-600 dark:text-blue-500 mt-0.5">아래 버튼으로 로그인해주세요.</p>
                  </div>
                  <button
                    onClick={() => {
                      const digits = phone.replace(/\D/g, '');
                      setLoginId(digits);
                      setMode('login');
                    }}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                  >
                    로그인하러 가기 <ChevronRight className="w-4 h-4" />
                  </button>
                  <button type="button" onClick={resetMemberFlow} className="w-full text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                    다른 번호로 시도
                  </button>
                </div>
              )}

              {step === 'found' && foundMember && (
                <form onSubmit={handleFoundSignup} className="space-y-4">
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg px-4 py-3">
                    <p className="text-sm font-medium text-green-700 dark:text-green-400">{foundMember.name}님, 반갑습니다!</p>
                    <p className="text-xs text-green-600 dark:text-green-500 mt-0.5">이미 등록된 회원입니다. 비밀번호를 설정해주세요.</p>
                  </div>
                  <div>
                    <label className="label">비밀번호</label>
                    <input type="password" className="input" placeholder="6자 이상" value={password} onChange={e => setPassword(e.target.value)} required />
                  </div>
                  <div>
                    <label className="label">비밀번호 확인</label>
                    <input type="password" className="input" placeholder="비밀번호 재입력" value={passwordConfirm} onChange={e => setPasswordConfirm(e.target.value)} required />
                  </div>
                  {memberError && <p className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400 rounded-lg px-3 py-2">{memberError}</p>}
                  <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2" disabled={memberLoading}>
                    {memberLoading ? '처리 중...' : '가입 완료'}
                  </button>
                </form>
              )}

              {step === 'success' && (
                <div className="text-center py-4 space-y-3">
                  <div className="w-14 h-14 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
                    <Check className="w-7 h-7 text-green-600 dark:text-green-400" />
                  </div>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    가입이 완료되었습니다!
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    지금 바로 로그인하실 수 있습니다.
                  </p>
                  <button className="btn-primary mx-auto" onClick={() => { setPhone(''); setPassword(''); setPasswordConfirm(''); setMemberError(''); setStep('member_login'); }}>로그인하러 가기</button>
                </div>
              )}
            </div>
          )}

          {/* ── 트레이너 로그인 ── */}
          {mode === 'trainer' && (
            <div className="card p-6">
              <div className="flex items-center gap-2 mb-5">
                <button onClick={() => { resetTrainerFlow(); setMode('login'); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h2 className="font-semibold text-gray-900 dark:text-white">트레이너 로그인</h2>
              </div>

              {trainerStep === 'gym_code' && (
                <form onSubmit={handleTrainerGymCode} className="space-y-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400">헬스장 매장 코드와 등록된 전화번호로 로그인하세요.</p>
                  <div>
                    <label className="label">매장 코드</label>
                    <input className="input text-center text-lg tracking-widest font-mono uppercase" placeholder="XXXXXX" value={trainerGymCode} onChange={e => setTrainerGymCode(e.target.value.toUpperCase())} maxLength={6} required />
                  </div>
                  {trainerError && <p className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400 rounded-lg px-3 py-2">{trainerError}</p>}
                  <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2" disabled={trainerLoading}>
                    {trainerLoading ? '확인 중...' : '다음'} <ChevronRight className="w-4 h-4" />
                  </button>
                </form>
              )}

              {trainerStep === 'phone' && (
                <form onSubmit={handleTrainerPhone} className="space-y-4">
                  <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg px-4 py-3 text-sm text-primary-700 dark:text-primary-300">
                    <span className="font-medium">{trainerGymInfo?.name}</span>
                  </div>
                  <div>
                    <label className="label">등록된 전화번호</label>
                    <input
                      type="tel"
                      className="input"
                      placeholder="010-0000-0000"
                      value={trainerPhone}
                      onChange={e => setTrainerPhone(formatPhone(e.target.value))}
                      required
                    />
                  </div>
                  {trainerError && <p className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400 rounded-lg px-3 py-2">{trainerError}</p>}
                  <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2" disabled={trainerLoading}>
                    {trainerLoading ? '확인 중...' : '다음'} <ChevronRight className="w-4 h-4" />
                  </button>
                </form>
              )}

              {trainerStep === 'signin' && trainerRecord && (
                <form onSubmit={handleTrainerSignIn} className="space-y-4">
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg px-4 py-3">
                    <p className="text-sm font-medium text-green-700 dark:text-green-400">{trainerRecord.name} 트레이너님</p>
                    <p className="text-xs text-green-600 dark:text-green-500 mt-0.5">비밀번호를 입력해주세요.</p>
                  </div>
                  <div>
                    <label className="label">비밀번호</label>
                    <input type="password" className="input" placeholder="비밀번호" value={trainerPw} onChange={e => setTrainerPw(e.target.value)} required />
                  </div>
                  {trainerError && <p className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400 rounded-lg px-3 py-2">{trainerError}</p>}
                  <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2" disabled={trainerLoading}>
                    {trainerLoading ? '로그인 중...' : '로그인'}
                  </button>
                </form>
              )}

              {trainerStep === 'signup' && trainerRecord && (
                <form onSubmit={handleTrainerSignUp} className="space-y-4">
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg px-4 py-3">
                    <p className="text-sm font-medium text-blue-700 dark:text-blue-400">{trainerRecord.name} 트레이너님, 환영합니다!</p>
                    <p className="text-xs text-blue-600 dark:text-blue-500 mt-0.5">처음 로그인하시는군요. 비밀번호를 설정해주세요.</p>
                  </div>
                  <div>
                    <label className="label">비밀번호 <span className="text-red-500">*</span></label>
                    <input type="password" className="input" placeholder="6자 이상" value={trainerPw} onChange={e => setTrainerPw(e.target.value)} required />
                  </div>
                  <div>
                    <label className="label">비밀번호 확인 <span className="text-red-500">*</span></label>
                    <input type="password" className="input" placeholder="비밀번호 재입력" value={trainerPwConfirm} onChange={e => setTrainerPwConfirm(e.target.value)} required />
                  </div>
                  {trainerError && <p className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400 rounded-lg px-3 py-2">{trainerError}</p>}
                  <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2" disabled={trainerLoading}>
                    {trainerLoading ? '처리 중...' : '시작하기'}
                  </button>
                </form>
              )}
            </div>
          )}

          {/* ── 트레이너 초대 코드 가입 ── */}
          {mode === 'trainer_invite' && (
            <div className="card p-6">
              <div className="flex items-center gap-2 mb-5">
                <button onClick={() => { resetTiFlow(); setMode('login'); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h2 className="font-semibold text-gray-900 dark:text-white">트레이너 초대 코드로 가입</h2>
              </div>

              {/* Step 1: 코드 입력 */}
              {tiStep === 'code' && (
                <form onSubmit={handleTiValidate} className="space-y-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    헬스장 대표자에게 받은 초대 코드를 입력해주세요.
                  </p>
                  <div>
                    <label className="label">초대 코드</label>
                    <input
                      className="input text-center tracking-widest text-lg font-mono uppercase"
                      value={tiCode}
                      onChange={e => setTiCode(e.target.value.toUpperCase())}
                      placeholder="XXXXXXXX"
                      maxLength={8}
                      required
                    />
                  </div>
                  {tiError && <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">{tiError}</p>}
                  <button type="submit" className="btn-primary w-full justify-center" disabled={tiLoading}>
                    {tiLoading ? '확인 중...' : '코드 확인'}
                  </button>
                </form>
              )}

              {/* Step 2: 정보 확인 */}
              {tiStep === 'info' && tiInfo && (
                <div className="space-y-4">
                  <div className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-xl">
                    <p className="text-xs text-primary-600 dark:text-primary-400 font-semibold mb-1">초대 확인됨</p>
                    <p className="text-base font-bold text-gray-900 dark:text-white">{tiInfo.trainerName} 트레이너님</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{tiInfo.gymName}</p>
                  </div>
                  <button
                    onClick={() => setTiStep('signup')}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                  >
                    계속 진행하기 <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Step 3: 이메일 + 비밀번호 */}
              {tiStep === 'signup' && (
                <form onSubmit={handleTiSignup} className="space-y-4">
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-semibold">{tiInfo?.trainerName}</span> · {tiInfo?.gymName}
                  </div>
                  <div>
                    <label className="label">이메일</label>
                    <input
                      type="email"
                      className="input"
                      placeholder="trainer@email.com"
                      value={tiEmail}
                      onChange={e => setTiEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="label">비밀번호</label>
                    <input
                      type="password"
                      className="input"
                      placeholder="6자 이상"
                      value={tiPw}
                      onChange={e => setTiPw(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="label">비밀번호 확인</label>
                    <input
                      type="password"
                      className="input"
                      placeholder="비밀번호 재입력"
                      value={tiPwConfirm}
                      onChange={e => setTiPwConfirm(e.target.value)}
                      required
                    />
                  </div>
                  {tiError && <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">{tiError}</p>}
                  <button type="submit" className="btn-primary w-full justify-center" disabled={tiLoading}>
                    {tiLoading ? '가입 중...' : '가입 완료'}
                  </button>
                </form>
              )}
            </div>
          )}

          {/* ── 비밀번호 재설정 ── */}
          {mode === 'reset' && (
            <div className="card p-6">
              <div className="flex items-center gap-2 mb-5">
                <button onClick={() => setMode('login')} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h2 className="font-semibold text-gray-900 dark:text-white">비밀번호 재설정</h2>
              </div>
              {resetSent ? (
                <div className="text-center py-4 space-y-3">
                  <div className="w-14 h-14 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
                    <Check className="w-7 h-7 text-green-600 dark:text-green-400" />
                  </div>
                  <p className="font-semibold text-gray-900 dark:text-white">이메일을 전송했습니다</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">받은 메일함을 확인해 링크를 클릭해주세요.</p>
                  <button className="btn-primary mx-auto" onClick={() => setMode('login')}>로그인으로 돌아가기</button>
                </div>
              ) : (
                <form onSubmit={handleReset} className="space-y-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400">가입한 이메일 주소를 입력하면 재설정 링크를 보내드립니다.</p>
                  <div>
                    <label className="label">이메일</label>
                    <input type="email" className="input" placeholder="admin@example.com" value={resetEmail} onChange={e => setResetEmail(e.target.value)} required />
                  </div>
                  {resetError && <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">{resetError}</p>}
                  <button type="submit" className="btn-primary w-full justify-center" disabled={resetLoading}>
                    {resetLoading ? '전송 중...' : '재설정 링크 받기'}
                  </button>
                </form>
              )}
            </div>
          )}

          {/* ── 대표자 가입 (초대 코드) ── */}
          {mode === 'owner' && (
            <div className="card p-6">
              <div className="flex items-center gap-2 mb-5">
                <button onClick={() => { resetOwnerFlow(); setMode('login'); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h2 className="font-semibold text-gray-900 dark:text-white">대표자 계정 가입</h2>
              </div>

              {ownerStep === 'code' ? (
                <form onSubmit={handleValidateCode} className="space-y-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400">관리자로부터 받은 초대 코드를 입력해주세요.</p>
                  <div>
                    <label className="label">초대 코드</label>
                    <input
                      className="input text-center text-lg tracking-widest font-mono uppercase"
                      placeholder="XXXXXXXX"
                      value={inviteCode}
                      onChange={e => setInviteCode(e.target.value.toUpperCase())}
                      maxLength={8}
                      required
                    />
                  </div>
                  {inviteCodeError && <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">{inviteCodeError}</p>}
                  <button type="submit" className="btn-primary w-full justify-center" disabled={inviteCodeLoading}>
                    {inviteCodeLoading ? '확인 중...' : '확인'} <ChevronRight className="w-4 h-4" />
                  </button>
                </form>
              ) : (
                <form onSubmit={handleOwnerSignup} className="space-y-5">
                  {/* 단계 인디케이터 */}
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex items-center gap-1.5">
                      <div className="w-5 h-5 rounded-full bg-primary-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">1</div>
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-200">계정 설정</span>
                    </div>
                    <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                    <div className="flex items-center gap-1.5">
                      <div className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-400 text-xs font-bold flex-shrink-0">2</div>
                      <span className="text-xs text-gray-400">헬스장 설정</span>
                    </div>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg px-4 py-2.5 flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                    <p className="text-xs text-green-700 dark:text-green-400 font-medium">초대 코드 확인 완료 · <span className="font-mono">{inviteCode}</span></p>
                  </div>

                  {/* 대표자 정보 */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">대표자 정보</p>
                    <div className="space-y-3">
                      <div>
                        <label className="label">대표자 이름 <span className="text-red-500">*</span></label>
                        <input className="input" placeholder="홍길동" value={ownerName} onChange={e => setOwnerName(e.target.value)} required />
                      </div>
                      <div>
                        <label className="label">휴대폰 번호 <span className="text-red-500">*</span></label>
                        <input
                          type="tel"
                          className="input"
                          placeholder="010-0000-0000"
                          value={ownerPhone}
                          onChange={e => setOwnerPhone(formatPhone(e.target.value))}
                          required
                        />
                      </div>
                      <div>
                        <label className="label">로그인 이메일 <span className="text-red-500">*</span></label>
                        <div className="flex gap-2">
                          <input
                            type="email"
                            className="input flex-1"
                            placeholder="owner@example.com"
                            value={ownerEmail}
                            onChange={e => { setOwnerEmail(e.target.value); setEmailChecked(false); setEmailCheckMsg(''); }}
                            required
                          />
                          <button
                            type="button"
                            onClick={handleCheckEmail}
                            disabled={emailCheckLoading}
                            className="px-3 py-2 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors whitespace-nowrap disabled:opacity-50"
                          >
                            {emailCheckLoading ? '확인 중...' : '중복확인'}
                          </button>
                        </div>
                        {emailCheckMsg && (
                          <p className={`text-xs mt-1 ${emailChecked ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
                            {emailCheckMsg}
                          </p>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="label">비밀번호 <span className="text-red-500">*</span></label>
                          <input type="password" className="input" placeholder="6자 이상" value={ownerPw} onChange={e => setOwnerPw(e.target.value)} required />
                        </div>
                        <div>
                          <label className="label">비밀번호 확인 <span className="text-red-500">*</span></label>
                          <input type="password" className="input" placeholder="재입력" value={ownerPwConfirm} onChange={e => setOwnerPwConfirm(e.target.value)} required />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 사업장 정보 */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">사업장 정보</p>
                    <div className="space-y-3">
                      <div>
                        <label className="label">사업자 상호명 <span className="text-red-500">*</span></label>
                        <input className="input" placeholder="○○ 피트니스" value={ownerBusinessName} onChange={e => setOwnerBusinessName(e.target.value)} required />
                      </div>
                      <div>
                        <label className="label">매장 대표번호</label>
                        <input
                          type="tel"
                          className="input"
                          placeholder="02-0000-0000"
                          value={ownerBusinessPhone}
                          onChange={e => setOwnerBusinessPhone(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="label">사업장 주소</label>
                        <div className="flex gap-2 mt-1">
                          <input
                            className="input flex-1"
                            placeholder="주소 검색 버튼을 클릭하세요"
                            value={ownerBusinessAddress}
                            readOnly
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if (!window.daum?.Postcode) {
                                setOwnerError('주소 검색 서비스를 불러오는 중입니다. 잠시 후 다시 시도해 주세요.');
                                return;
                              }
                              new window.daum.Postcode({
                                oncomplete: (data) => {
                                  setOwnerBusinessAddress(data.roadAddress || data.jibunAddress);
                                  setOwnerBusinessAddressDetail('');
                                }
                              }).open();
                            }}
                            className="px-3 py-2 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors whitespace-nowrap"
                          >
                            주소 검색
                          </button>
                        </div>
                        {ownerBusinessAddress && (
                          <input
                            className="input mt-2"
                            placeholder="상세 주소 입력 (동/호수 등)"
                            value={ownerBusinessAddressDetail}
                            onChange={e => setOwnerBusinessAddressDetail(e.target.value)}
                          />
                        )}
                      </div>
                      <div>
                        <label className="label">사업자등록번호</label>
                        <input
                          className="input"
                          placeholder="000-00-00000"
                          value={ownerBusinessNumber}
                          onChange={e => setOwnerBusinessNumber(formatBusinessNumber(e.target.value))}
                          maxLength={12}
                        />
                      </div>
                      <div>
                        <label className="label">세금계산서 수신 이메일</label>
                        <input type="email" className="input" placeholder="tax@example.com" value={ownerTaxEmail} onChange={e => setOwnerTaxEmail(e.target.value)} />
                      </div>
                    </div>
                  </div>

                  {/* 사업자등록증 업로드 */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">서류 제출</p>
                    <div>
                      <label className="label">사업자등록증 <span className="text-xs text-gray-400 font-normal">(선택 — 유료 전환 시 제출 가능)</span></label>
                      {businessDoc ? (
                        <div className="mt-1 flex items-center gap-3 px-4 py-3 rounded-xl border border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/20">
                          <FileText className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                          <span className="flex-1 text-sm text-primary-700 dark:text-primary-300 truncate">{businessDocName}</span>
                          <button
                            type="button"
                            onClick={() => { setBusinessDoc(null); setBusinessDocName(''); }}
                            className="text-gray-400 hover:text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="mt-1 grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => cameraInputRef.current?.click()}
                            className="flex flex-col items-center gap-2 py-4 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-primary-400 dark:hover:border-primary-600 transition-colors text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                          >
                            <Camera className="w-6 h-6" />
                            <span className="text-xs font-medium">카메라 촬영</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="flex flex-col items-center gap-2 py-4 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-primary-400 dark:hover:border-primary-600 transition-colors text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                          >
                            <Upload className="w-6 h-6" />
                            <span className="text-xs font-medium">보관함 선택</span>
                          </button>
                        </div>
                      )}
                      <input
                        ref={cameraInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={handleDocFile}
                      />
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,.pdf"
                        className="hidden"
                        onChange={handleDocFile}
                      />
                      <p className="text-xs text-gray-400 mt-1.5">JPG, PNG, PDF (최대 10MB)</p>
                    </div>
                  </div>

                  {ownerError && <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">{ownerError}</p>}
                  <button type="submit" className="btn-primary w-full justify-center" disabled={ownerLoading}>
                    {ownerLoading ? '처리 중...' : '다음 — 헬스장 설정'}
                  </button>
                  <p className="text-xs text-gray-400 text-center">
                    가입 후 24시간 무료 체험이 즉시 시작됩니다.
                  </p>
                </form>
              )}
            </div>
          )}

        </div>
      </main>

      {/* 푸터 */}
      <footer className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-6 text-center">
        <div className="flex justify-center items-center gap-4 mb-3 text-xs">
          <Link to="/terms" className="font-medium text-gray-700 dark:text-gray-300 hover:underline">이용약관</Link>
          <Link to="/privacy" className="font-medium text-gray-700 dark:text-gray-300 hover:underline">개인정보처리방침</Link>
        </div>
        <div className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed space-y-0.5">
          <p>
            <span className="font-medium text-gray-500 dark:text-gray-400">티앤티 주식회사 (TNT Inc.)</span>
            <span className="mx-1.5">|</span>사업자등록번호: 105-88-17712
          </p>
          <p>충북 충주시 호암토성2로3, 707호 (27480)</p>
          <p>고객문의: 1877-1827</p>
        </div>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
          Copyright © 2026 티앤티 주식회사. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
